/**
 * CloseSessionModal — closes the open session and surfaces the Z-report écart
 * (POST /caisse/sessions/:id/cloture, cap finance.caisse.close). The close is definitive (warned).
 * After close, the returned session + Z-report are shown read-only until the user dismisses.
 */
import { useState, type FormEvent } from "react";
import { Badge, Button, Card, FormField, Modal, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { FINANCE_CAPS, useCloseSession, useLabId } from "../api";
import type { SessionCaisse, SessionStats } from "../types";
import { MoneyInput } from "./MoneyInput";
import { Money } from "./Money";

export interface CloseSessionModalProps {
  open: boolean;
  session: SessionCaisse;
  stats?: SessionStats | undefined;
  onClose: () => void;
  onClosed: () => void;
}

export function CloseSessionModal({ open, session, stats, onClose, onClosed }: CloseSessionModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canClose = useCan(FINANCE_CAPS.caisseClose);
  const mutation = useCloseSession(labId);

  const [comptees, setComptees] = useState<number | null>(null);
  const [touched, setTouched] = useState(false);
  const [closed, setClosed] = useState<SessionCaisse | null>(null);

  // The theoretical drawer is the server's especes_attendues (fond + encaissé − remboursé), DERIVED
  // server-side — the only 'theorique' on the wire. Fall back to the session's opening float so the
  // preview still renders before the stats query resolves.
  const theorique = stats?.especes_attendues ?? session.fond_de_caisse;

  const comptesError =
    touched && (comptees == null || comptees < 0) ? t("finance.field.amountInvalid") : null;
  const valid = comptees != null && comptees >= 0;
  // Live (pre-close) estimate only; the authoritative écart is server-frozen and read from the
  // cloture response below. Skip the estimate when we have no theoretical to compare against.
  const ecart = valid && theorique != null ? comptees - theorique : null;

  const handleClose = () => {
    if (mutation.isPending) return;
    setComptees(null);
    setTouched(false);
    setClosed(null);
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canClose) return;
    mutation.mutate(
      { sessionId: session.id, especes_comptees: comptees ?? 0 },
      {
        onSuccess: (data) => {
          toast.success(t("finance.caisse.close.success"));
          setClosed(data);
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.caisse.close.title"));
        },
      },
    );
  };

  // Phase 2 — the session is closed: render the Z from the FLAT cloture response. The wire has no
  // nested rapport_z; read closed.* directly and prefer the server-frozen ecart (never recompute).
  // cloture does NOT return caisse_id, so carry it from the pre-close session prop.
  if (closed) {
    return (
      <Modal
        open={open}
        onClose={() => {
          handleClose();
          onClosed();
        }}
        title={t("finance.caisse.z.title")}
        footer={
          <Button
            onClick={() => {
              handleClose();
              onClosed();
            }}
          >
            {t("action.close")}
          </Button>
        }
      >
        <Card title={`${t("finance.caisse.caisseId")} ${session.caisse_id ?? "—"}`}>
          <ZReportRows
            fond={closed.fond_ouverture ?? session.fond_de_caisse}
            encaisse={closed.total_encaisse ?? stats?.total_encaisse}
            comptees={closed.fond_fermeture ?? undefined}
            ecart={closed.ecart}
          />
        </Card>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("finance.caisse.close.title")}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("action.close")}
          </Button>
          <Button
            type="submit"
            form="finance-close-session"
            variant="danger"
            loading={mutation.isPending}
            disabled={!canClose}
          >
            {t("finance.caisse.close.submit")}
          </Button>
        </>
      }
    >
      <form id="finance-close-session" onSubmit={submit} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <p
            role="note"
            style={{
              margin: 0,
              padding: "var(--nv-s-3)",
              borderRadius: "var(--nv-r-md)",
              background: "var(--nv-tint-high)",
              color: "var(--nv-warn)",
              fontSize: "var(--nv-fs-label)",
            }}
          >
            ▲ {t("finance.caisse.close.warn")}
          </p>

          <dl style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "var(--nv-s-2)", margin: 0 }}>
            <dt style={{ color: "var(--nv-slate)" }}>{t("finance.caisse.fond")}</dt>
            <dd style={{ margin: 0, textAlign: "end" }}>
              <Money centimes={session.fond_de_caisse} />
            </dd>
            <dt style={{ color: "var(--nv-slate)" }}>{t("finance.caisse.stats.theorique")}</dt>
            <dd style={{ margin: 0, textAlign: "end" }}>
              <Money centimes={theorique} />
            </dd>
          </dl>

          <FormField
            label={t("finance.caisse.close.comptees")}
            hint={t("finance.caisse.close.comptees.hint")}
            required
            {...(comptesError ? { error: comptesError } : {})}
          >
            {(p) => (
              <MoneyInput
                {...p}
                autoFocus
                aria-label={t("finance.amount.aria")}
                valueCentimes={comptees}
                onChangeCentimes={setComptees}
                invalid={!!comptesError}
              />
            )}
          </FormField>

          {ecart != null && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ color: "var(--nv-slate)" }}>{t("finance.caisse.z.ecart")}</span>
              <EcartBadge ecart={ecart} />
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

function EcartBadge({ ecart }: { ecart: number }) {
  const { t } = useT();
  if (ecart === 0) return <Badge tone="success">{t("finance.caisse.z.ecart.none")}</Badge>;
  const tone = ecart > 0 ? "info" : "danger";
  const label = ecart > 0 ? t("finance.caisse.z.ecart.surplus") : t("finance.caisse.z.ecart.short");
  return (
    <Badge tone={tone}>
      {label} <Money centimes={ecart} signed />
    </Badge>
  );
}

function ZReportRows({
  fond,
  encaisse,
  comptees,
  ecart,
}: {
  fond: number | undefined;
  encaisse: number | undefined;
  comptees: number | undefined;
  ecart: number | null | undefined;
}) {
  const { t } = useT();
  return (
    <dl style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "var(--nv-s-2)", margin: 0 }}>
      <dt style={{ color: "var(--nv-slate)" }}>{t("finance.caisse.fond")}</dt>
      <dd style={{ margin: 0, textAlign: "end" }}>
        <Money centimes={fond} />
      </dd>
      <dt style={{ color: "var(--nv-slate)" }}>{t("finance.caisse.stats.total")}</dt>
      <dd style={{ margin: 0, textAlign: "end" }}>
        <Money centimes={encaisse} />
      </dd>
      <dt style={{ color: "var(--nv-slate)" }}>{t("finance.caisse.close.comptees")}</dt>
      <dd style={{ margin: 0, textAlign: "end" }}>
        <Money centimes={comptees} />
      </dd>
      <dt style={{ color: "var(--nv-slate)", paddingTop: "var(--nv-s-2)" }}>
        {t("finance.caisse.z.ecart")}
      </dt>
      <dd style={{ margin: 0, textAlign: "end", paddingTop: "var(--nv-s-2)" }}>
        {ecart == null ? <Money centimes={null} /> : <EcartBadge ecart={ecart} />}
      </dd>
    </dl>
  );
}
